import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Shield, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";

const UI_STRINGS: Record<string, { heading: string; subtext: (title: string, idx: number, total: number) => string; footer: string; accept: string }> = {
  en: {
    heading: "Policy Update Required",
    subtext: (title, idx, total) => `Please review and accept the ${title} (${idx} of ${total})`,
    footer: "By accepting, you agree to abide by these terms.",
    accept: "I Accept",
  },
  ar: {
    heading: "مطلوب الموافقة على السياسة",
    subtext: (title, idx, total) => `يرجى مراجعة وقبول ${title} (${idx} من ${total})`,
    footer: "بقبولك، فإنك توافق على الالتزام بهذه الشروط.",
    accept: "أوافق",
  },
  fr: {
    heading: "Mise à jour de la politique requise",
    subtext: (title, idx, total) => `Veuillez lire et accepter ${title} (${idx} sur ${total})`,
    footer: "En acceptant, vous acceptez de respecter ces conditions.",
    accept: "J'accepte",
  },
  es: {
    heading: "Actualización de política requerida",
    subtext: (title, idx, total) => `Revise y acepte ${title} (${idx} de ${total})`,
    footer: "Al aceptar, usted acepta cumplir con estos términos.",
    accept: "Acepto",
  },
};

export function PolicyConsentGate({ children }: { children: React.ReactNode }) {
  const { user, role } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const strings = UI_STRINGS[lang] || UI_STRINGS.en;
  const isRtl = lang === "ar";

  const [pendingPolicies, setPendingPolicies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    checkPolicies();
  }, [user]);

  const checkPolicies = async () => {
    try {
      // Get all active policies
      const { data: activePolicies } = await supabase
        .from("legal_policies")
        .select("id, policy_type, version, title, content, title_ar, title_fr, title_es, content_ar, content_fr, content_es")
        .eq("is_active", true);
      
      if (!activePolicies?.length) { setLoading(false); return; }

      // Get user's acceptances
      const { data: acceptances } = await supabase
        .from("policy_acceptances")
        .select("policy_id")
        .eq("user_id", user!.id);
      
      // If user has accepted ANY policy before, skip entirely (only show on first signup)
      if (acceptances && acceptances.length > 0) {
        setLoading(false);
        return;
      }
      
      // New user — show all active policies for first-time acceptance
      setPendingPolicies(activePolicies);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedField = (policy: any, field: string) => {
    if (lang === "en") return policy[field];
    const localized = policy[`${field}_${lang}`];
    return localized || policy[field]; // fallback to English
  };

  const acceptPolicy = async (policy: any) => {
    setAccepting(true);
    try {
      const { error } = await supabase.from("policy_acceptances").insert({
        user_id: user!.id,
        policy_id: policy.id,
        policy_type: policy.policy_type,
        policy_version: policy.version,
        user_agent: navigator.userAgent,
      });
      if (error) throw error;
      
      if (currentIndex < pendingPolicies.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setPendingPolicies([]);
      }
    } catch (err: any) {
      toast.error("Failed to record acceptance: " + err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return null;
  if (!pendingPolicies.length) return <>{children}</>;

  const current = pendingPolicies[currentIndex];
  const localTitle = getLocalizedField(current, "title");
  const localContent = getLocalizedField(current, "content");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-4"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{strings.heading}</h2>
              <p className="text-xs text-muted-foreground">
                {strings.subtext(localTitle, currentIndex + 1, pendingPolicies.length)}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_p]:text-foreground/80 [&_li]:text-foreground/80">
            <ReactMarkdown>{localContent}</ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/50 bg-card/50">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              {strings.footer} Version {current.version}.
            </p>
            <button
              onClick={() => acceptPolicy(current)}
              disabled={accepting}
              className="flex items-center gap-2 btn-gradient text-primary-foreground px-6 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
            >
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {strings.accept}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
